import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardBody, CardHeader, Button, Input } from '../ui';
import { organizationAPI } from '../../api';
import { useToast } from '../../contexts/ToastContext';

/* V4 defaults — mirrors backend PROPOSAL_BRANDING_DEFAULTS / frontend V4_DEFAULTS.
   Used for the Reset to Defaults action and as a safe initial form state. */
const DEFAULT_COLORS = {
  primary_color: '#21436b',
  secondary_color: '#5b8c52',
  background_color: '#FFFFFF',
  card_background_color: '#FFFFFF',
  secondary_text_color: '#647B97',
};

const COLOR_FIELDS = [
  { key: 'primary_color', label: 'Primary (headings)' },
  { key: 'secondary_color', label: 'Accent (buttons, bullets)' },
  { key: 'background_color', label: 'Page background' },
  { key: 'card_background_color', label: 'Card background' },
  { key: 'secondary_text_color', label: 'Secondary text (body)' },
];

const HEX_RE = /^#(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

/* Logo upload/preview row. Module-scoped (not nested in the parent component) so
   React keeps the same component type across renders — otherwise the subtree, and
   the hidden file input, would remount on every parent re-render. */
const LogoRow = ({ logoType, label, fallbackHint, fileRef, url, isAdmin, pending, disabled, onUpload, onClear }) => (
  <div>
    <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">{label}</p>
    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{fallbackHint}</p>
    <div className="flex items-center gap-4">
      <div className="w-28 h-14 flex items-center justify-center rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 overflow-hidden">
        {url ? (
          <img src={url} alt={label} className="max-h-12 max-w-full object-contain" />
        ) : (
          <span className="text-[10px] text-gray-400">Default</span>
        )}
      </div>
      {isAdmin && (
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              onUpload(logoType, e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <Button
            size="sm"
            variant="secondary"
            disabled={disabled}
            onClick={() => fileRef.current?.click()}
          >
            Upload
          </Button>
          {url && (
            <Button
              size="sm"
              variant="secondary"
              disabled={disabled}
              onClick={() => onClear(logoType)}
            >
              Remove
            </Button>
          )}
          {pending && (
            <span className="text-xs text-amber-600 dark:text-amber-400">Unsaved — click Save to apply</span>
          )}
        </div>
      )}
    </div>
  </div>
);

/**
 * Shared settings tab for per-organization V4 proposal branding.
 * Mounted on OrganizationDetailPage, the single canonical org settings page (both superadmin and org admin).
 * `isAdmin=false` renders a read-only view.
 */
const ProposalBrandingTab = ({ orgId, isAdmin = true }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [colors, setColors] = useState(DEFAULT_COLORS);
  const [logos, setLogos] = useState({ primary_logo_url: null, secondary_logo_url: null, favicon_url: null, pdf_branding_logo_url: null });
  // Staged-but-unsaved logo changes, applied on Save. null = no change for that slot.
  // Shape: { action: 'upload', file, previewUrl } | { action: 'clear' }
  const [stagedLogos, setStagedLogos] = useState({ primary: null, secondary: null, favicon: null, pdf_branding: null });

  const primaryFileRef = useRef(null);
  const secondaryFileRef = useRef(null);
  const pdfBrandingFileRef = useRef(null);
  // Track every object URL we create so we can revoke them on unmount (avoids leaks).
  const objectUrlsRef = useRef([]);

  useEffect(() => () => objectUrlsRef.current.forEach(URL.revokeObjectURL), []);

  const clearStaged = useCallback(() => {
    setStagedLogos((prev) => {
      Object.values(prev).forEach((s) => s?.previewUrl && URL.revokeObjectURL(s.previewUrl));
      return { primary: null, secondary: null, favicon: null, pdf_branding: null };
    });
  }, []);

  const applyBranding = useCallback((data) => {
    setColors({
      primary_color: data.primary_color || DEFAULT_COLORS.primary_color,
      secondary_color: data.secondary_color || DEFAULT_COLORS.secondary_color,
      background_color: data.background_color || DEFAULT_COLORS.background_color,
      card_background_color: data.card_background_color || DEFAULT_COLORS.card_background_color,
      secondary_text_color: data.secondary_text_color || DEFAULT_COLORS.secondary_text_color,
    });
    setLogos({
      primary_logo_url: data.primary_logo_url || null,
      secondary_logo_url: data.secondary_logo_url || null,
      favicon_url: data.favicon_url || null,
      pdf_branding_logo_url: data.pdf_branding_logo_url || null,
    });
  }, []);

  const fetchBranding = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const data = await organizationAPI.getProposalBranding(orgId);
      applyBranding(data);
    } catch {
      toast.error('Failed to load proposal branding');
    } finally {
      setLoading(false);
    }
  }, [orgId, applyBranding, toast]);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  const invalidKey = COLOR_FIELDS.find((f) => !HEX_RE.test(colors[f.key]));

  // Maps a logo slot to its saved-URL key. Favicon is its own branding slot, so it
  // doesn't follow the `${type}_logo_url` convention the primary/secondary logos share.
  const savedUrlKey = { primary: 'primary_logo_url', secondary: 'secondary_logo_url', favicon: 'favicon_url', pdf_branding: 'pdf_branding_logo_url' };

  // What to show in a logo slot: a staged change (if any) takes precedence over the saved URL.
  const displayLogoUrl = (logoType) => {
    const staged = stagedLogos[logoType];
    if (staged?.action === 'upload') return staged.previewUrl;
    if (staged?.action === 'clear') return null;
    return logos[savedUrlKey[logoType]];
  };

  // Stage a logo upload locally; nothing is sent to the server until Save.
  const handleStageUpload = (logoType, file) => {
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    objectUrlsRef.current.push(previewUrl);
    setStagedLogos((prev) => {
      if (prev[logoType]?.previewUrl) URL.revokeObjectURL(prev[logoType].previewUrl);
      return { ...prev, [logoType]: { action: 'upload', file, previewUrl } };
    });
  };

  // Stage a logo removal locally; applied on Save.
  const handleStageClear = (logoType) => {
    setStagedLogos((prev) => {
      if (prev[logoType]?.previewUrl) URL.revokeObjectURL(prev[logoType].previewUrl);
      return { ...prev, [logoType]: { action: 'clear' } };
    });
  };

  const handleSave = async () => {
    if (invalidKey) {
      toast.error(`${invalidKey.label} must be a 3- or 6-digit hex color`);
      return;
    }
    setSaving(true);
    try {
      let updated = await organizationAPI.updateProposalBranding(orgId, colors);
      for (const logoType of ['primary', 'secondary', 'pdf_branding']) {
        const staged = stagedLogos[logoType];
        if (staged?.action === 'upload') {
          updated = await organizationAPI.uploadProposalLogo(orgId, logoType, staged.file);
        } else if (staged?.action === 'clear') {
          updated = await organizationAPI.clearProposalLogo(orgId, logoType);
        }
      }
      clearStaged();
      applyBranding(updated);
      toast.success('Proposal branding saved');
    } catch {
      toast.error('Failed to save proposal branding');
    } finally {
      setSaving(false);
    }
  };

  // Reset = restore default colors (PUT) + clear the proposal logo slots
  // (primary, secondary, pdf_branding) with a DELETE each. The favicon is org
  // identity now (managed in Organization Settings), so it is intentionally
  // left untouched.
  const handleReset = async () => {
    setResetting(true);
    try {
      await organizationAPI.updateProposalBranding(orgId, DEFAULT_COLORS);
      await organizationAPI.clearProposalLogo(orgId, 'primary');
      await organizationAPI.clearProposalLogo(orgId, 'secondary');
      const updated = await organizationAPI.clearProposalLogo(orgId, 'pdf_branding');
      clearStaged();
      applyBranding(updated);
      toast.success('Branding reset to defaults');
    } catch {
      toast.error('Failed to reset branding');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardBody>
          <p className="text-center text-gray-500 dark:text-gray-400 py-4">Loading…</p>
        </CardBody>
      </Card>
    );
  }

  const busy = saving || resetting;

  return (
    <Card>
      <CardHeader>
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Proposal Branding</h2>
      </CardHeader>
      <CardBody className="space-y-6">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Customize the colors and logos clients see on the V4 proposal preview. Other proposal
          layouts are unaffected.
        </p>

        {/* Colors */}
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-3">Colors</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {COLOR_FIELDS.map((f) => {
              const value = colors[f.key];
              const valid = HEX_RE.test(value);
              return (
                <div key={f.key}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {f.label}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={valid ? value : '#000000'}
                      disabled={!isAdmin}
                      onChange={(e) => setColors((c) => ({ ...c, [f.key]: e.target.value }))}
                      className="h-9 w-10 rounded border border-gray-200 dark:border-gray-700 bg-transparent disabled:opacity-50"
                    />
                    <Input
                      value={value}
                      disabled={!isAdmin}
                      onChange={(e) => setColors((c) => ({ ...c, [f.key]: e.target.value }))}
                    />
                  </div>
                  {!valid && (
                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                      Must be a 3- or 6-digit hex color (e.g. #21436b).
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Live preview strip */}
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Preview</p>
          <div
            className="rounded-lg border border-gray-200 dark:border-gray-700 p-5"
            style={{ background: HEX_RE.test(colors.background_color) ? colors.background_color : '#FFFFFF' }}
          >
            <h3
              className="text-lg font-semibold"
              style={{ color: HEX_RE.test(colors.primary_color) ? colors.primary_color : '#21436b' }}
            >
              Investment{' '}
              <span
                style={{
                  color: HEX_RE.test(colors.secondary_color) ? colors.secondary_color : '#5b8c52',
                  fontStyle: 'italic',
                }}
              >
                Proposal
              </span>
            </h3>
            <p
              className="mt-2 text-sm max-w-md"
              style={{ color: HEX_RE.test(colors.secondary_text_color) ? colors.secondary_text_color : '#647B97' }}
            >
              A unique investment opportunity with proven risk-adjusted returns and a strong
              deployment pipeline.
            </p>
            <div
              className="mt-3 inline-block rounded-md px-4 py-3"
              style={{
                background: HEX_RE.test(colors.card_background_color) ? colors.card_background_color : '#FFFFFF',
                border: '1px solid rgba(0,0,0,0.08)',
              }}
            >
              <span
                className="text-sm font-semibold"
                style={{ color: HEX_RE.test(colors.secondary_color) ? colors.secondary_color : '#5b8c52' }}
              >
                $12.4M
              </span>
              <span className="ml-2 text-xs text-gray-500">Target raise</span>
            </div>
            <button
              type="button"
              className="ml-3 mt-3 inline-block rounded-md px-4 py-2 text-sm font-semibold text-white"
              style={{ background: HEX_RE.test(colors.secondary_color) ? colors.secondary_color : '#5b8c52' }}
            >
              Continue
            </button>
          </div>
        </div>

        {/* Logos */}
        <div className="space-y-5">
          <LogoRow
            logoType="primary"
            label="Primary logo"
            fallbackHint="Shown in the proposal header. Falls back to the Default logo when not set."
            fileRef={primaryFileRef}
            url={displayLogoUrl('primary')}
            isAdmin={isAdmin}
            pending={!!stagedLogos.primary}
            disabled={busy}
            onUpload={handleStageUpload}
            onClear={handleStageClear}
          />
          <LogoRow
            logoType="secondary"
            label="Secondary logo"
            fallbackHint="Used as the icon/watermark. Falls back to the Default icon when not set."
            fileRef={secondaryFileRef}
            url={displayLogoUrl('secondary')}
            isAdmin={isAdmin}
            pending={!!stagedLogos.secondary}
            disabled={busy}
            onUpload={handleStageUpload}
            onClear={handleStageClear}
          />
          <LogoRow
            logoType="pdf_branding"
            label="PDF branding logo"
            fallbackHint="Drawn faded in the four corners of every diligence report page. Falls back to the secondary logo, then the primary logo."
            fileRef={pdfBrandingFileRef}
            url={displayLogoUrl('pdf_branding')}
            isAdmin={isAdmin}
            pending={!!stagedLogos.pdf_branding}
            disabled={busy}
            onUpload={handleStageUpload}
            onClear={handleStageClear}
          />
        </div>

        {isAdmin && (
          <div className="flex justify-between pt-2">
            <Button variant="secondary" onClick={handleReset} disabled={busy}>
              {resetting ? 'Resetting…' : 'Reset to Defaults'}
            </Button>
            <Button onClick={handleSave} disabled={busy || !!invalidKey}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default ProposalBrandingTab;
